import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputComponent } from '@ui/input/input.component';
import { ModalComponent } from '@ui/modal/modal.component';
import { MODAL_REF, ModalRef, ModalService } from '@ui/modal/modal.service';
import { RequestsService } from '@api/services/requests.service';
import { RequestDetail } from '@api/models/request.model';
import { LoadingService } from '@ui/app-layout/loading-service';

@Component({
  selector: 'app-edit-deal-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    InputComponent,
    ReactiveFormsModule,
  ],
  template: `
    <ui-modal
      [title]="'Referenties bewerken'"
      [submitText]="'Opslaan'"
      [cancelText]="'Annuleren'"
      [disabled]="saving()"
      [loading]="saving()"
      (close)="closeModal()"
      (save)="saveModal()"
    >
      <form [formGroup]="dealForm" class="space-y-4">
        <div>
          <ui-input
            label="SO referentie"
            [formControl]="soReferenceControl"
            [required]="false"
            placeholder="Service en Onderhoud referentie"
          ></ui-input>
        </div>
        <div>
          <ui-input
            label="Factuur referentie"
            [formControl]="factuurReferenceControl"
            [required]="false"
            placeholder="Optionele referentie voor uw administratie"
          ></ui-input>
        </div>
      </form>

      @if(errorMessage()) {
      <div class="text-danger-500 mt-4">{{ errorMessage() }}</div>
      }
    </ui-modal>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditDealModalComponent {
  private fb = inject(FormBuilder);
  private modalRef = inject(MODAL_REF);
  private modalService = inject(ModalService);
  private requestsService = inject(RequestsService);
  private loadingService = inject(LoadingService);

  @Input() request: RequestDetail | null = null;

  dealForm: FormGroup = this.fb.group({
    wmo_so_reference: [''],
    factuurreferentie: [''],
  });

  saving = signal(false);
  errorMessage = signal<string | undefined>(undefined);

      ngOnInit() {
    // Vul de form met request.details waarden (als die er zijn), anders leeg
    this.dealForm.patchValue({
      wmo_so_reference: this.request?.details?.wmo_so_reference ?? '',
      factuurreferentie: this.request?.details?.factuurreferentie ?? '',
    });
  }

  get soReferenceControl() {
    return this.dealForm.get('wmo_so_reference') as any;
  }

  get factuurReferenceControl() {
    return this.dealForm.get('factuurreferentie') as any;
  }

  closeModal() {
    this.modalService.close(this.modalRef as ModalRef<any>);
  }

  saveModal() {
    if (!this.request?.id) return;

    this.dealForm.markAllAsTouched();
    if (this.dealForm.valid) {
      this.saving.set(true);
      this.errorMessage.set(undefined);

            const dealData: { wmo_so_reference?: string; factuurreferentie?: string } = {};

      // Voeg alleen gewijzigde velden toe
      const currentSoRef = this.request?.details?.wmo_so_reference ?? '';
      const currentFactuurRef = this.request?.details?.factuurreferentie ?? '';
      const newSoRef = this.soReferenceControl?.value ?? '';
      const newFactuurRef = this.factuurReferenceControl?.value ?? '';

      if (newSoRef !== currentSoRef) {
        dealData.wmo_so_reference = newSoRef;
      }
      if (newFactuurRef !== currentFactuurRef) {
        dealData.factuurreferentie = newFactuurRef;
      }

      // Alleen updaten als er wijzigingen zijn
      if (Object.keys(dealData).length > 0) {
        this.requestsService.updateRequestReferences(this.request.id, dealData).subscribe({
          next: () => {
            this.saving.set(false);
            this.modalService.close(this.modalRef as ModalRef<any>);
          },
          error: (error) => {
            this.saving.set(false);
            this.errorMessage.set(error.error?.message || 'Er is een fout opgetreden bij het opslaan');
          }
        });
      } else {
        // Geen wijzigingen, sluit modal
        this.saving.set(false);
        this.modalService.close(this.modalRef as ModalRef<any>);
      }
    }
  }
}
